import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Certificate } from '../models/certificate.model';
import { CertificatesApiRequest, CertificatesApiResponse, CertificateApiItem } from '../models/certificates-api-response.model';
import { environment } from '../../environments/environment';
import { CipherService } from '../core/services/cipher.service';

@Injectable({
  providedIn: 'root'
})
export class CertificateService {

  constructor(
    private http: HttpClient,
    private cipher: CipherService
  ) {}

  /**
   * Obtiene los certificados del usuario (Token Virtual) - Nuevo endpoint.
   * POST /api/certificates con body { nuip, password }.
   * Si cipher está configurado, envía { data: "<blob cifrado>" } y descifra la respuesta.
   */
  getCertificates(nuip: string, password: string): Observable<CertificatesApiResponse> {
    const body: CertificatesApiRequest = { nuip: nuip.trim(), password };
    const url = environment.certificatesApiUrl ?? `${environment.apiBaseUrl}/api/certificates`;

    if (!this.cipher.isEncryptionEnabled) {
      return this.http.post<CertificatesApiResponse>(url, body);
    }

    return from(this.cipher.encrypt(JSON.stringify(body))).pipe(
      switchMap((encrypted) =>
        this.http.post<{ data?: string } | CertificatesApiResponse>(url, { data: encrypted })
      ),
      switchMap((response) => this.unwrapCipherResponse(response))
    );
  }

  /**
   * Extrae CN/identidad de un PFX/P12 (firma visible P10).
   * POST /api/certificates/pfx/identity
   */
  inspectPfxIdentity(pfx: File | Blob, password: string, alias?: string): Observable<{
    success: boolean;
    cn: string;
    subject?: string;
    serial?: string;
    serialNumber?: string;
  }> {
    const form = new FormData();
    form.append('pfx', pfx, pfx instanceof File ? pfx.name : 'certificate.pfx');
    form.append('pfxPassword', password);
    if (alias?.trim()) {
      form.append('alias', alias.trim());
    }
    const url = `${environment.apiBaseUrl}/api/certificates/pfx/identity`;
    return this.http.post<{
      success: boolean;
      cn: string;
      subject?: string;
      serial?: string;
      serialNumber?: string;
    }>(url, form);
  }

  /**
   * Mapea un certificado del API al modelo Certificate de la app.
   */
  mapApiCertificateToModel(item: CertificateApiItem): Certificate {
    return {
      cn: item.cn,
      serial: item.serial,
      serialNumber: item.serialNumber,
      validity: {
        notBefore: item.notBefore,
        notAfter: item.notAfter
      },
      o: item.o,
      ou: item.ou,
      issuer: item.issuer
    };
  }

  /**
   * Envía archivos al backend para obtener certificados
   * @param files Archivos seleccionados
   * @param nuip Documento del usuario
   * @param password Contraseña
   * @param type Tipo de firma: 'pdf' o 'cades'
   */
  uploadFiles(
    files: File[],
    nuip: string,
    password: string,
    type: 'pdf' | 'cades'
  ): Observable<{ certificates: Certificate[]; pdfBase64s: string[] }> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    formData.append('nuip', nuip);
    formData.append('password', password);

    const endpoint = `${environment.apiBaseUrl}/upload/${type}`;
    return this.http.post<{ certificates: Certificate[]; pdfBase64s: string[]; }>(endpoint, formData);
  }

  private unwrapCipherResponse(
    response: { data?: string } | CertificatesApiResponse
  ): Observable<CertificatesApiResponse> {
    const wrapped = response as { data?: string };
    if (wrapped?.data && typeof wrapped.data === 'string') {
      return from(this.cipher.decrypt(wrapped.data)).pipe(
        map((plain) => JSON.parse(plain) as CertificatesApiResponse)
      );
    }
    return of(response as CertificatesApiResponse);
  }

}
